<template>
    <!--
        UI-24-1 — sustainability room messaging, shown per room in the accommodation block.
        - ZERO rooms: "Added Services" note + the guest-added amenities (service.roomAmenities),
          each with a per-night price (price.totalPrice / nights).
        - SUSTAINABLE rooms: "Omitted Services" note + the omitted amenities (room-type config:
          basicEnabled && !enabled).
        The (i) icon opens a modal listing the room's INCLUDED amenities (room-type config: enabled).
        Rendered only for own-product SUSTAINABLE/ZERO rooms.
    -->
    <div
        v-if="isOwn && (level === 'SUSTAINABLE' || level === 'ZERO')"
        class="room-sustainability-note"
        data-cn="RoomSustainabilityNote"
    >
        <v-divider class="mb-3" />
        <div class="d-flex align-center mb-1">
            <v-icon start size="small">mdi-leaf</v-icon>
            <span class="text-subtitle-2 font-weight-bold">
                {{
                    level === 'SUSTAINABLE'
                        ? $t('order_sustainability.omitted_services')
                        : $t('order_sustainability.added_services')
                }}
            </span>
            <v-icon size="small" class="ml-1" color="info" @click="showModal = true">mdi-information-outline</v-icon>
        </div>
        <p class="text-body-2 mb-2">
            {{
                level === 'SUSTAINABLE'
                    ? $t('order_sustainability.note_sustainable')
                    : $t('order_sustainability.note_zero')
            }}
        </p>
        <div class="amenities-list d-flex flex-wrap">
            <!-- ZERO: guest-added amenities with per-night price -->
            <v-chip
                v-for="a in addedAmenities"
                :key="`add-${a.id}`"
                size="small"
                class="ma-1"
                color="success"
                variant="outlined"
            >
                <v-icon start size="x-small">mdi-check-circle</v-icon>
                {{ a.name }}
                <span v-if="a.count && a.count > 1" class="ml-1">({{ a.count }})</span>
                <span v-if="amenityAmount(a)" class="d-inline-flex align-center ml-1 text-grey-darken-1">
                    <span class="mr-1 text-grey-darken-2">|</span>
                    + {{ $filters.priceFormat(amenityAmount(a), amenityCurrency(a))
                    }}<template v-if="showPerNightSuffix(a)">{{
                        $t('order_sustainability.per_night_suffix')
                    }}</template>
                </span>
            </v-chip>
            <!-- SUSTAINABLE: omitted amenities -->
            <v-chip
                v-for="a in omittedAmenities"
                :key="`omit-${a.id}`"
                size="small"
                class="ma-1"
                color="error"
                variant="outlined"
            >
                <v-icon start size="x-small">mdi-close-circle</v-icon>
                {{ a.name }}
                <!-- Omitted (red) pills: name only — no price, minus, or separator (UI-24-1). -->
            </v-chip>
        </div>

        <!-- Included amenities modal -->
        <ModalWrapper v-model="showModal" :max-width="500" hide-actions>
            <template v-slot:title>{{ $t('order_sustainability.modal_title') }}</template>
            <template v-slot:content>
                <div v-if="!includedAmenities.length" class="text-body-2">—</div>
                <div v-else class="room-sustainability-note__amenities">
                    <div
                        v-for="a in includedAmenities"
                        :key="`inc-${a.id}`"
                        class="room-sustainability-note__amenity text-body-2"
                    >
                        <span class="mr-1">•</span>
                        <span>{{ a.name }}</span>
                    </div>
                </div>
            </template>
        </ModalWrapper>
    </div>
</template>

<script setup lang="ts">
    import {
        isOwnProductService,
        loadRoomTypeConfig,
    } from '~~/customization_layers/zsh/app/composables/useOrderRoomSustainability'

    const props = defineProps<{
        room?: any
        service?: any
        nights?: number
    }>()

    const showModal = ref(false)
    const config = ref<{omitted: any[]; included: any[]} | null>(null)

    const level = computed(() => props.room?.sustainabilityLevel)
    const hotelId = computed(() => props.service?.serviceDetails?.[0]?.hotelId)
    const roomTypeId = computed(() => props.room?.roomTypeId)
    const isOwn = computed(() => isOwnProductService(props.service))
    // ZERO rooms also include any booked extra services (e.g. Tablet) as "Added Services".
    // Normalized to the roomAmenities shape so they render with the same pill.
    const addedAmenities = computed(() => {
        if (level.value !== 'ZERO') return []
        // roomAmenities are per-stay → shown per-night (divide by nights).
        const roomAms = (props.service?.roomAmenities || []).map((a: any) => ({...a, perNight: true}))
        // Booked extra services (e.g. Tablet) are per-booking (flat) → do NOT divide by nights.
        const extras = (props.service?.extraServices || []).map((es: any) => {
            const cp = es?.prices?.find((p: any) => p.priceType === 'CLIENT')
            return {
                id: `es-${es.id}`,
                name: es.name,
                count: es.quantity,
                price: cp ? {value: cp.amount, currency: cp.currency} : null,
                perNight: false,
            }
        })
        return [...roomAms, ...extras]
    })
    const omittedAmenities = computed(() => (level.value === 'SUSTAINABLE' ? config.value?.omitted || [] : []))
    const includedAmenities = computed(() => config.value?.included || [])

    // Omitted-amenity config prices are currency-less; fall back to the service's CLIENT currency.
    const currency = computed(
        () => props.service?.salesTerms?.find((p: any) => p.type === 'CLIENT')?.price?.currency || ''
    )
    // Per-stay price of an amenity/extras. roomAmenities & extras use {value, currency};
    // omitted config amenities use a bare number.
    const amenityTotal = (a: any): number =>
        Number(a?.price?.value ?? a?.price?.totalPrice ?? (typeof a?.price === 'number' ? a.price : 0))
    const amenityCurrency = (a: any): string => a?.price?.currency || currency.value
    // Display amount: per-night equivalent for per-stay items (room amenities, omitted) on
    // multi-night stays; flat for per-booking extras (perNight === false). null when no price.
    const amenityAmount = (a: any): number | null => {
        const total = amenityTotal(a)
        if (!total) return null
        if (a?.perNight === false) return total
        const n = Number(props.nights ?? 0)
        return n > 1 ? total / n : total
    }
    // "/per night" only for per-night items on multi-night stays (not per-booking extras).
    const showPerNightSuffix = (a: any): boolean => a?.perNight !== false && Number(props.nights ?? 0) > 1

    onMounted(async () => {
        if (!isOwn.value) return
        if (level.value !== 'SUSTAINABLE' && level.value !== 'ZERO') return
        config.value = await loadRoomTypeConfig(hotelId.value, roomTypeId.value)
    })
</script>

<style scoped>
    .room-sustainability-note__amenities {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 4px 12px;
    }

    .room-sustainability-note__amenity {
        display: flex;
        align-items: baseline;
        word-break: break-word;
    }
</style>
